import {useLegacyGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {
  BrandV1_likeBrandLazy,
  BrandV1_unlikeBrandLazy,
} from '@aboutyou/grpc-clients/grips/protos/brands/brand_v1.grpc.lazy';
import {CustomerResponse} from '@aboutyou/grpc-clients/grips/protos/deprecated/customer.types';
import {
  IdolV1_likeIdolLazy,
  IdolV1_unlikeIdolLazy,
} from '@aboutyou/grpc-clients/grips/protos/idol/idol_v1.grpc.lazy';
import {
  OutfitService_LikeOutfitLazy,
  OutfitService_UnlikeOutfitLazy,
} from '@aboutyou/grpc-clients/grips/protos/outfits/v1/outfit_service.grpc.lazy';
import {Outfit} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/outfit.types';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
// eslint-disable-next-line monorepo/no-internal-import
import {useTrackContentInteraction} from '@aboutyou/tracking/src/trackingHooks/trackContentInteraction';
import {useCallback, useEffect, useRef} from 'react';
import {useCustomerResponse} from '../customer/customer.hooks';
import {useUserToken} from '../userStore/user.selector';

export const useIdolFollowToggleCallback = (idolId: number) => {
  const {data} = useCustomerResponse();
  const authToken = useUserToken();
  const likedIdols = data?.idols?.liked ?? [];
  const isFollowed = Boolean(idolId && likedIdols.includes(idolId)) ?? false;

  const followIdol = useIdolsFollowCallback(idolId);
  const unFollowIdol = useIdolsUnfollowCallback(idolId);

  return useCallback(() => {
    if (isFollowed) {
      unFollowIdol({
        idolId,
        customerSession: {
          authToken: authToken!,
        },
      });
    } else {
      followIdol({
        idolId,
        customerSession: {
          authToken: authToken!,
        },
      });
    }
  }, [authToken, followIdol, idolId, isFollowed, unFollowIdol]);
};

const useIdolsFollowCallback = (idolId: number) => {
  const {data: favorites, updateCache} = useCustomerResponse();

  return useLegacyGrpcRequestCallback(IdolV1_likeIdolLazy, {
    onSuccess: () => {
      if (favorites && idolId) {
        updateCache({
          ...favorites,
          idols: {
            liked: [...(favorites.idols?.liked ?? []), idolId],
            disliked: favorites.idols?.disliked ?? [],
          },
        });
      }
    },
  }).mutateAsync;
};

const useIdolsUnfollowCallback = (idolId: number) => {
  const {data: favorites, updateCache} = useCustomerResponse();

  return useLegacyGrpcRequestCallback(IdolV1_unlikeIdolLazy, {
    onSuccess: () => {
      if (favorites) {
        updateCache({
          ...favorites,
          idols: {
            liked: [...(favorites.idols?.liked ?? [])].filter(
              id => id !== idolId,
            ),
            disliked: favorites.idols?.disliked ?? [],
          },
        });
      }
    },
  }).mutateAsync;
};

export const useOutfitLikeToggleCallback = (outfit: Outfit) => {
  const {data} = useCustomerResponse();
  const authToken = useUserToken();
  const likedOutfits = data?.outfits?.liked ?? [];
  const isLiked =
    Boolean(outfit.id && likedOutfits.includes(outfit.id)) ?? false;

  const likeOutfit = useOutfitsLikeCallback(outfit.id);
  const dislikeOutfit = useOutfitsDislikeCallback(outfit.id);

  const trackContentInteraction = useTrackContentInteraction({
    contentId: outfit.id,
    contentName: outfit.name,
    contentPath: outfit.path,
    type: 'outfit',
  });

  return useCallback(() => {
    if (isLiked) {
      dislikeOutfit({
        outfitId: outfit.id,
        customerSession: {
          authToken: authToken!,
        },
      });
      trackContentInteraction('dislike');
    } else {
      likeOutfit({
        outfitId: outfit.id,
        customerSession: {
          authToken: authToken!,
        },
      });
      trackContentInteraction('like');
    }
  }, [isLiked, dislikeOutfit, outfit.id, authToken, likeOutfit]);
};

const useOutfitsLikeCallback = (outfitId: number) => {
  const {data: favorites, updateCache} = useCustomerResponse();

  return useLegacyGrpcRequestCallback(OutfitService_LikeOutfitLazy, {
    onSuccess: () => {
      if (favorites) {
        updateCache({
          ...favorites,
          outfits: {
            liked: [...(favorites.outfits?.liked ?? []), outfitId],
            disliked: favorites.outfits?.disliked ?? [],
          },
        });
      }
    },
  }).mutateAsync;
};

const useOutfitsDislikeCallback = (outfitId: number) => {
  const {data: favorites, updateCache} = useCustomerResponse();

  return useLegacyGrpcRequestCallback(OutfitService_UnlikeOutfitLazy, {
    onSuccess: () => {
      if (favorites) {
        updateCache({
          ...favorites,
          outfits: {
            liked: [...(favorites.outfits?.liked ?? [])].filter(
              id => id !== outfitId,
            ),
            disliked: favorites.outfits?.disliked ?? [],
          },
        });
      }
    },
  }).mutateAsync;
};

export interface FollowableBrand {
  id: number;
  name: string;
  path: string;
}

export const useBrandToggleFollow = ({
  brand,
  promotionTracker,
  onFollowSuccess,
}: {
  brand: FollowableBrand;
  promotionTracker?: PromotionTracker;
  onFollowSuccess?: VoidFunction;
}) => {
  const {data, updateCache, refetch} = useCustomerResponse();
  const authToken = useUserToken();
  const authTokenRef = useRef(authToken);
  const dataRef = useRef(data);
  const likedBrands = data?.brands?.liked ?? [];
  const brandId = brand.id;
  const isFollowed = likedBrands.includes(brandId);

  // Keep the ref updated with the latest authToken
  useEffect(() => {
    authTokenRef.current = authToken;
  }, [authToken]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const onFollowBrandSuccess = () => {
    const latestFavorites = dataRef.current;
    if (latestFavorites) {
      const latestLiked = latestFavorites.brands?.liked ?? [];
      updateBrandsCache(latestFavorites, updateCache, [
        ...latestLiked,
        brandId,
      ]);
    }
    void refetch();
    trackContentInteraction('like');
    onFollowSuccess?.();
  };

  const onUnfollowBrandSuccess = () => {
    const latestFavorites = dataRef.current;
    if (latestFavorites) {
      const latestLiked = latestFavorites.brands?.liked ?? [];
      updateBrandsCache(
        latestFavorites,
        updateCache,
        latestLiked.filter(id => id !== brandId),
      );
    }
    void refetch();
    trackContentInteraction('unlike');
  };

  const followBrand = useFollowBrand(
    data,
    updateCache,
    brandId,
    onFollowBrandSuccess,
  );
  const unfollowBrand = useUnfollowBrand(
    data,
    updateCache,
    brandId,
    onUnfollowBrandSuccess,
  );

  const trackContentInteraction = useTrackContentInteraction({
    contentId: brandId,
    contentName: brand.name,
    contentPath: brand.path,
    type: 'brand',
    ...promotionTracker,
  });

  const toggleFollow = useCallback(() => {
    if (isFollowed) {
      unfollowBrand({
        brandId,
        customerSession: {
          authToken: authTokenRef.current!,
        },
      });
    } else {
      followBrand({
        brandId,
        customerSession: {
          authToken: authTokenRef.current!,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authTokenRef.current,
    brandId,
    isFollowed,
    data,
    updateCache,
    likedBrands,
    unfollowBrand,
    trackContentInteraction,
    followBrand,
  ]);

  return {isFollowed, toggleFollow};
};

const updateBrandsCache = (
  favorites: CustomerResponse | undefined,
  updateCache: (data: CustomerResponse | undefined) => void,
  likedBrands: number[],
) => {
  if (favorites) {
    updateCache({
      ...favorites,
      brands: {
        ...favorites.brands,
        liked: likedBrands,
      },
    });
  }
};

const useFollowBrand = (
  favorites: CustomerResponse | undefined,
  updateCache: (data: CustomerResponse | undefined) => void,
  brandId: number,
  onSuccess: VoidFunction,
) => {
  return useLegacyGrpcRequestCallback(BrandV1_likeBrandLazy, {
    onError() {
      if (favorites) {
        updateBrandsCache(
          favorites,
          updateCache,
          [...(favorites.brands?.liked ?? [])].filter(id => id !== brandId),
        );
      }
    },
    onSuccess,
  }).mutateAsync;
};

const useUnfollowBrand = (
  favorites: CustomerResponse | undefined,
  updateCache: (data: CustomerResponse | undefined) => void,
  brandId: number,
  onSuccess: VoidFunction,
) => {
  return useLegacyGrpcRequestCallback(BrandV1_unlikeBrandLazy, {
    onError() {
      if (favorites) {
        updateBrandsCache(favorites, updateCache, [
          ...(favorites.brands?.liked ?? []),
          brandId,
        ]);
      }
    },
    onSuccess,
  }).mutateAsync;
};
